import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi, type AdwordsHour } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { LineChart } from '../../components/LineChart.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { num } from '../../lib/format.js';

/**
 * 주문 — 원본 /home/bananapartner/xadm/ord_list.php.
 *
 * 요일별 날짜 목록과 시간대별 누적/증분(주문·slim 주문·매출·slim 매출·유입·애드워즈)을
 * 유지하면서, 동일 요일의 최근 변화와 시간대 상세를 빠르게 읽는 대시보드로 구성한다.
 *
 * 애드워즈 열은 애드워즈 현황 페이지와 같은 /xadm/adwords/hours(누적 스냅샷)를
 * 받아 여기서 증분을 낸다 - 주문 시간대 API 는 건드리지 않는다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAY_NAMES = WEEKDAYS.map((day) => `${day}요일`);

function hourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function signed(value: number) {
  return `${value > 0 ? '+' : ''}${num(value)}`;
}

const rate = (value: number | null) => (value === null ? '-' : `${value}%`);

type Metric = { cumulative: number; delta: number };

interface AdsHour {
  click: Metric;
  cost: Metric;
  /** 원본의 애드워즈 '건수/매출' 은 파트너 전환(porder/pconvcost) 이다. */
  order: Metric;
  sale: Metric;
  /** 원본이 '수익율' 이라 부르던 광고비 ÷ 전환매출 */
  rate: { cumulative: number | null; delta: number | null };
}

/** 원본 round(cost / conv * 100, 1). 분모가 0 이면 원본은 INF 를 찍었다 - 여기선 '-'. */
function costRate(cost: number, sale: number): number | null {
  return sale > 0 ? Math.round((cost / sale) * 1000) / 10 : null;
}

/**
 * 원본 :327 - 애드워즈 누적 스냅샷을 시각별로 펴고 앞 행과의 차를 그 시간의 증분으로 쓴다.
 * 원본은 바로 앞 시각(hour-1)을 뺐는데, 그 시각이 비면 누적 전체가 증분으로 찍혔다.
 */
function adsByHour(rows: AdwordsHour[]): Map<number, AdsHour> {
  const out = new Map<number, AdsHour>();
  let prev: AdwordsHour | null = null;
  for (const row of rows) {
    const metric = (pick: (r: AdwordsHour) => number): Metric => ({
      cumulative: pick(row),
      delta: pick(row) - (prev ? pick(prev) : 0),
    });
    const cost = metric((r) => r.cost);
    const sale = metric((r) => r.pconvcost);
    out.set(row.hour, {
      click: metric((r) => r.click),
      cost,
      order: metric((r) => r.porder),
      sale,
      rate: { cumulative: costRate(cost.cumulative, sale.cumulative), delta: costRate(cost.delta, sale.delta) },
    });
    prev = row;
  }
  return out;
}

export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const weekdayParam = params.get('weekday');
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [pinnedDate, setPinnedDate] = useState<string | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDate = hoverDate ?? pinnedDate;

  const days = useQuery({
    queryKey: ['xadm-order-days', weekdayParam],
    queryFn: () => xadmApi.orderDays(weekdayParam === null ? undefined : Number(weekdayParam)),
  });

  const hours = useQuery({
    queryKey: ['xadm-order-hours', openDate],
    queryFn: () => xadmApi.orderHours(openDate!),
    enabled: openDate !== null,
  });

  const adwords = useQuery({
    queryKey: ['xadm-adwords-hours', openDate],
    queryFn: () => xadmApi.adwordsHours(openDate!),
    enabled: openDate !== null,
  });
  const adsHours = useMemo(() => adsByHour(adwords.data?.hours ?? []), [adwords.data]);
  const adsEmpty = <span className="order-hour-ads__empty">{adwords.isLoading ? '…' : '-'}</span>;

  const selectedWeekday = weekdayParam === null ? (days.data?.weekday ?? null) : Number(weekdayParam);
  const openDay = days.data?.days.find((day) => day.date === openDate) ?? null;
  const summary = useMemo(() => {
    const rows = days.data?.days ?? [];
    const latest = rows[0] ?? null;
    const previous = rows[1] ?? null;
    const recentFour = rows.slice(0, 4);
    const trend = [...rows.slice(0, 12)].reverse();
    const delta = latest && previous ? latest.orderCount - previous.orderCount : null;
    return {
      dayCount: rows.length,
      latest,
      delta,
      deltaRate: delta === null || !previous?.orderCount ? null : (delta / previous.orderCount) * 100,
      average: recentFour.length === 0
        ? 0
        : Math.round(recentFour.reduce((sum, row) => sum + row.orderCount, 0) / recentFour.length),
      averageOrderValue: latest?.orderCount ? Math.round(latest.sales / latest.orderCount) : 0,
      peak: trend.length === 0 ? null : trend.reduce((max, row) => row.orderCount > max.orderCount ? row : max),
      trend,
    };
  }, [days.data]);

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = null;
  };

  const previewDate = (date: string) => {
    cancelHoverClose();
    setHoverDate(date);
  };

  const schedulePreviewClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverDate(null), 180);
  };

  const togglePinnedDate = (date: string) => {
    cancelHoverClose();
    setPinnedDate((current) => current === date ? null : date);
  };

  const selectWeekday = (weekday: number) => {
    setHoverDate(null);
    setPinnedDate(null);
    update({ weekday });
  };

  useEffect(() => () => cancelHoverClose(), []);

  const trendLabels = summary.trend.map((day) => day.date);
  const trendSeries = [
    { id: 'orders', label: '주문', points: summary.trend.map((day) => ({ x: day.date, y: day.orderCount })) },
  ];

  return (
    <section className="visit-page order-page">
      <header className="page-head visit-page__head">
        <div>
          <h1 className="page-title">주문 통계</h1>
          <p className="page-desc">같은 요일의 주문과 매출 변화를 비교하고 시간대별 흐름까지 빠르게 탐색합니다.</p>
        </div>
        <span className="visit-page__source"><i aria-hidden="true" /> HOURLY ORDER LOG</span>
      </header>

      <nav className="visit-weekdays" aria-label="조회 요일">
        <span className="visit-weekdays__label">요일 선택</span>
        <div className="visit-weekdays__buttons">
          {WEEKDAYS.map((label, weekday) => (
            <button
              key={weekday}
              type="button"
              className={weekday === selectedWeekday ? 'visit-weekday visit-weekday--active' : 'visit-weekday'}
              aria-pressed={weekday === selectedWeekday}
              title={WEEKDAY_NAMES[weekday]}
              onClick={() => selectWeekday(weekday)}
            >
              {label}
            </button>
          ))}
        </div>
        <p>선택한 요일끼리 주문 성과를 비교합니다.</p>
      </nav>

      {!days.isLoading && !days.isError && summary.dayCount > 0 && (
        <>
          <div className="visit-summary" aria-label="주문 핵심 지표">
            <article className="visit-summary__item visit-summary__item--primary">
              <span>최근 주문</span>
              <strong>{num(summary.latest?.orderCount ?? 0)}</strong>
              <small>{`기준 ${summary.latest?.date}`} · {WEEKDAY_NAMES[selectedWeekday ?? 0]}</small>
            </article>
            <article className={`visit-summary__item visit-summary__item--delta ${summary.delta !== null && summary.delta < 0 ? 'visit-summary__item--down' : ''}`}>
              <span>직전 동일 요일 대비</span>
              <strong>{summary.delta === null ? '—' : signed(summary.delta)}</strong>
              <small>{summary.deltaRate === null ? '비교 데이터 없음' : `${summary.deltaRate > 0 ? '+' : ''}${summary.deltaRate.toFixed(1)}% 변화`}</small>
            </article>
            <article className="visit-summary__item order-summary__sales">
              <span>최근 매출</span>
              <strong>{num(summary.latest?.sales ?? 0)}</strong>
              <small>최근 4회 주문 평균 {num(summary.average)}건</small>
            </article>
            <article className="visit-summary__item order-summary__aov">
              <span>최근 객단가</span>
              <strong>{num(summary.averageOrderValue)}</strong>
              <small>매출 ÷ 주문 건수</small>
            </article>
          </div>

          <section className="visit-trend order-trend" aria-label="최근 주문 추이">
            <header className="visit-trend__head">
              <div>
                <span>TREND</span>
                <h2>최근 {summary.trend.length}회 주문 추이</h2>
                <p>선택한 {WEEKDAY_NAMES[selectedWeekday ?? 0]}의 주문 건수를 비교합니다.</p>
              </div>
              {summary.peak && (
                <div className="visit-trend__peak">
                  <span>최근 최고</span>
                  <strong>{num(summary.peak.orderCount)}</strong>
                  <small>{`기준 ${summary.peak.date}`}</small>
                </div>
              )}
            </header>
            <LineChart series={trendSeries} labels={trendLabels} height={220} />
          </section>
        </>
      )}

      <div className="visit-list-head">
        <div>
          <h2>{selectedWeekday === null ? '주문 기록' : `${WEEKDAY_NAMES[selectedWeekday]} 주문 기록`}</h2>
          <p>날짜 행에 마우스를 올리면 오른쪽에서 시간대별 주문·매출·유입·애드워즈를 바로 확인할 수 있습니다.</p>
        </div>
        {!days.isLoading && !days.isError && <span>{num(summary.dayCount)}개 날짜</span>}
      </div>

      {days.isLoading ? (
        <Spinner />
      ) : days.isError ? (
        <ErrorBox error={days.error} />
      ) : days.data!.days.length === 0 ? (
        <EmptyBox />
      ) : (
        <div className="visit-table-wrap">
          <table className="table order-table">
            <thead>
              <tr>
                <th aria-label="상세 열기" />
                <th>일자</th>
                <th>요일</th>
                <th>주문</th>
                <th>매출</th>
                <th>객단가</th>
              </tr>
            </thead>
            <tbody>
              {days.data!.days.map((day) => {
                const isOpen = openDate === day.date;
                return (
                  <tr
                    key={day.date}
                    className={isOpen ? 'table__row--clickable visit-table__row visit-table__row--open' : 'table__row--clickable visit-table__row'}
                    onMouseEnter={() => previewDate(day.date)}
                    onMouseLeave={schedulePreviewClose}
                    onClick={() => togglePinnedDate(day.date)}
                  >
                    <td className="visit-table__toggle">
                      <button
                        type="button"
                        aria-label={`${day.date} 시간대 상세 ${pinnedDate === day.date ? '닫기' : '고정해서 열기'}`}
                        aria-expanded={isOpen}
                        onClick={(event) => {
                          event.stopPropagation();
                          togglePinnedDate(day.date);
                        }}
                      >
                        <span aria-hidden="true">›</span>
                      </button>
                    </td>
                    <td className="visit-table__date">{day.date}</td>
                    <td><span className={`visit-week-badge visit-week-badge--${day.weekday}`}>{WEEKDAYS[day.weekday]}</span></td>
                    <td className="td-right order-table__orders">{num(day.orderCount)}</td>
                    <td className="td-right order-table__sales">{num(day.sales)}</td>
                    <td className="td-right order-table__aov">{num(day.orderCount ? Math.round(day.sales / day.orderCount) : 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openDate && openDay && (
        <aside
          className="visit-preview order-preview"
          aria-label={`${openDay.date} 시간대별 주문`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={schedulePreviewClose}
        >
          <section className="visit-detail">
            <header className="visit-detail__head">
              <div>
                <span>{WEEKDAY_NAMES[openDay.weekday]}</span>
                <h3>{openDay.date} 시간대별 주문</h3>
              </div>
              <div className="visit-detail__actions">
                <strong>주문 {num(openDay.orderCount)} · 매출 {num(openDay.sales)}</strong>
                <button
                  type="button"
                  aria-label="시간대별 주문 닫기"
                  onClick={() => {
                    setHoverDate(null);
                    setPinnedDate(null);
                  }}
                >
                  ×
                </button>
              </div>
            </header>
            {adwords.isError && (
              <p className="order-preview__ads-error" role="alert">
                애드워즈 자료를 불러오지 못했습니다: {adwords.error instanceof Error ? adwords.error.message : String(adwords.error)}
              </p>
            )}
            {hours.isLoading ? (
              <Spinner />
            ) : hours.isError ? (
              <ErrorBox error={hours.error} />
            ) : (hours.data?.hours.length ?? 0) === 0 ? (
              <EmptyBox />
            ) : (
              <div className="visit-detail__table-wrap">
                <table className="table table--nested order-hour-table">
                  <thead>
                    <tr>
                      <th>시간대</th>
                      <th>주문</th>
                      <th>매출</th>
                      <th>유입</th>
                      <th className="order-hour-ads order-hour-ads--start"><span>애드워즈</span>클릭</th>
                      <th className="order-hour-ads"><span>애드워즈</span>광고비</th>
                      <th className="order-hour-ads"><span>애드워즈</span>전환</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(hours.data?.hours ?? []).map((hour) => {
                      const ads = adsHours.get(hour.hour);
                      return (
                        <tr key={hour.hour}>
                          <td><span className="visit-hour">{hourLabel(hour.hour)}</span></td>
                          <td>
                            <div className="order-hour-metric">
                              <strong>{signed(hour.order.delta)}</strong>
                              <small>누적 {num(hour.order.cumulative)}</small>
                              <em>SLIM {signed(hour.orderSlim.delta)} · 누적 {num(hour.orderSlim.cumulative)}</em>
                            </div>
                          </td>
                          <td>
                            <div className="order-hour-metric order-hour-metric--sales">
                              <strong>{signed(hour.sale.delta)}</strong>
                              <small>누적 {num(hour.sale.cumulative)}</small>
                              <em>SLIM {signed(hour.saleSlim.delta)} · 누적 {num(hour.saleSlim.cumulative)}</em>
                            </div>
                          </td>
                          <td>
                            <div className="order-hour-metric order-hour-metric--visit">
                              <strong>{signed(hour.visit.delta)}</strong>
                              <small>누적 {num(hour.visit.cumulative)}</small>
                            </div>
                          </td>
                          <td className="order-hour-ads order-hour-ads--start">
                            {ads ? (
                              <div className="order-hour-metric order-hour-metric--ads-click">
                                <strong>{signed(ads.click.delta)}</strong>
                                <small>누적 {num(ads.click.cumulative)}</small>
                              </div>
                            ) : adsEmpty}
                          </td>
                          <td className="order-hour-ads">
                            {ads ? (
                              <div className="order-hour-metric order-hour-metric--ads-cost">
                                <strong>{signed(ads.cost.delta)}</strong>
                                <small>누적 {num(ads.cost.cumulative)}</small>
                                <em>광고비율 {rate(ads.rate.delta)} · 누적 {rate(ads.rate.cumulative)}</em>
                              </div>
                            ) : adsEmpty}
                          </td>
                          <td className="order-hour-ads">
                            {ads ? (
                              <div className="order-hour-metric order-hour-metric--ads-sale">
                                <strong>{signed(ads.sale.delta)}</strong>
                                <small>누적 {num(ads.sale.cumulative)}</small>
                                <em>{signed(ads.order.delta)}건 · 누적 {num(ads.order.cumulative)}건</em>
                              </div>
                            ) : adsEmpty}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </aside>
      )}
    </section>
  );
}
